/**
 * Turning a ZIP code into a {@link SiteLocalization}.
 *
 * ## What this is, and what it is not
 *
 * US ZIP prefixes are geographically ordered — 0 in New England, 9 on the Pacific — so the first
 * three digits place a site to within roughly a county. That is enough to answer the questions this
 * app asks a location: which winter, how long a season, which regional flora. It is nowhere near
 * enough to describe a garden, and the profile it produces says so: every entry resolves with
 * confidence `estimated` and a label naming the *district*, not the address.
 *
 * The table below is built into the bundle rather than fetched, and that is the feature. Localizing a
 * site is the first thing a user does and it must work offline, instantly, with no key, no rate
 * limit, and no third party learning where anyone's garden is. A network geocoder can only improve
 * the *precision* of two numbers this table already supplies.
 *
 * ## Where the numbers come from
 *
 * Hardiness zones follow the 2023 USDA revision, taken at a representative populated place inside
 * each block. Heat zones are AHS bands (average annual days above 30 °C). Frost dates are **not**
 * stored: they are derived from the zone by `estimateFrostWindow`, because publishing a frost table
 * this coarse would imply a precision the block does not have — and because a profile that later
 * carries real normals should overwrite one number, not two hundred.
 *
 * A block is a range of prefixes with one reading. Where a state spans real climatic difference
 * (Michigan, Arizona, California) it is split; where it does not (Mississippi, Iowa) it is one row.
 * Prefixes with no row — Puerto Rico, the military ZIPs, unassigned blocks — resolve to `undefined`
 * rather than to a neighbour, and the UI says the code is not in the table.
 *
 * ## The extension point
 *
 * {@link PostalResolver} is the interface a real service implements. `resolvePostalCode` is the
 * built-in one; swapping in a lookup against a live zone raster means implementing that one function
 * and returning `confidence: 'measured'`. Nothing downstream — resolver, renderer, ecology, UI —
 * knows or cares which produced the profile.
 */

import {
  LOCALIZATION_SCHEMA_VERSION,
  estimateFrostWindow,
  zoneFromValue,
  type EcoregionRef,
  type HardinessZone,
  type NativeRegionCode,
  type SiteLocalization,
} from './SiteLocalization';

/**
 * One block of ZIP prefixes with a single climatic reading.
 *
 * Tuple rather than object purely for density — this is a data table, and a hundred rows of
 * `{ from: 100, to: 104, … }` would bury the values it exists to show. The shape is fixed by
 * {@link ZoneRow} and read in exactly one place, {@link findRow}.
 */
type ZoneRow = readonly [
  /** First ZIP3 prefix in the block, inclusive. */
  from: number,
  /** Last ZIP3 prefix, inclusive. */
  to: number,
  /** What to call the place, in the form the summary reads. */
  label: string,
  latitude: number,
  longitude: number,
  /** Hardiness as a zone value: 6.5 is 6b. See `zoneValue`. */
  zone: number,
  region: NativeRegionCode,
  /** AHS heat zone: average annual days above 30 °C, banded 1–12. */
  heatZone: number,
  /** Broad ecological region. Names follow EPA Level III usage; no code is claimed. */
  ecoregion: string,
];

/**
 * The table. Ordered by prefix, which is also roughly north-east to south-west.
 *
 * Gaps are deliberate: 006–009 (Puerto Rico and the Virgin Islands), 090–098 (military Europe),
 * 340 (military Americas), 962–966 (military Pacific) and the unassigned prefixes have no row,
 * because this app's flora catalog has nothing sensible to say about a tropical island or an APO
 * address, and inventing a zone for one would be worse than declining.
 */
const ZONE_TABLE: readonly ZoneRow[] = [
  [5, 5, 'Long Island, New York', 40.81, -73.05, 7.5, 'mid-atlantic', 4, 'Northeastern Coastal Zone'],
  [10, 13, 'Western Massachusetts', 42.3, -72.6, 5.5, 'northeast', 3, 'Northeastern Highlands'],
  [14, 19, 'Eastern Massachusetts', 42.5, -71.2, 6.5, 'northeast', 4, 'Northeastern Coastal Zone'],
  [20, 24, 'Greater Boston', 42.36, -71.06, 7.0, 'northeast', 4, 'Northeastern Coastal Zone'],
  [25, 27, 'Cape Cod and the South Shore', 41.7, -70.4, 7.5, 'northeast', 3, 'Atlantic Coastal Pine Barrens'],
  [28, 29, 'Rhode Island', 41.72, -71.44, 7.0, 'northeast', 4, 'Northeastern Coastal Zone'],
  [30, 34, 'Southern New Hampshire', 42.9, -71.4, 6.0, 'northeast', 3, 'Northeastern Coastal Zone'],
  [35, 38, 'Northern New Hampshire', 44.2, -71.6, 5.0, 'northeast', 2, 'Northeastern Highlands'],
  [39, 43, 'Southern Maine', 43.7, -70.3, 5.5, 'northeast', 2, 'Laurentian Plains and Hills'],
  [44, 47, 'Central Maine', 44.8, -69.0, 5.0, 'northeast', 2, 'Laurentian Plains and Hills'],
  [48, 49, 'Northern Maine', 46.7, -68.0, 4.0, 'northeast', 1, 'Northeastern Highlands'],
  [50, 54, 'Southern Vermont', 43.2, -72.6, 5.5, 'northeast', 2, 'Northeastern Highlands'],
  [55, 59, 'Northern Vermont', 44.5, -72.6, 5.0, 'northeast', 2, 'Northeastern Highlands'],
  [60, 64, 'Northern Connecticut', 41.8, -72.7, 6.5, 'northeast', 4, 'Northeastern Coastal Zone'],
  [65, 69, 'Coastal Connecticut', 41.2, -73.2, 7.0, 'northeast', 4, 'Northeastern Coastal Zone'],
  [70, 79, 'Northern New Jersey', 40.75, -74.2, 7.0, 'mid-atlantic', 5, 'Northern Piedmont'],
  [80, 84, 'Southern New Jersey', 39.7, -74.9, 7.5, 'mid-atlantic', 6, 'Atlantic Coastal Pine Barrens'],
  [85, 89, 'Central New Jersey', 40.3, -74.6, 6.5, 'mid-atlantic', 5, 'Northern Piedmont'],
  [100, 104, 'New York City', 40.71, -74.0, 7.5, 'mid-atlantic', 5, 'Northeastern Coastal Zone'],
  [105, 109, 'The Hudson Valley', 41.3, -73.9, 6.5, 'northeast', 4, 'Northeastern Coastal Zone'],
  [110, 119, 'Long Island', 40.8, -73.4, 7.5, 'mid-atlantic', 5, 'Atlantic Coastal Pine Barrens'],
  [120, 129, "New York's Capital District", 42.7, -73.8, 5.5, 'northeast', 3, 'Northeastern Highlands'],
  [130, 139, 'Central New York', 43.0, -76.1, 5.5, 'northeast', 3, 'Erie Drift Plain'],
  [140, 149, 'Western New York', 42.9, -78.8, 6.0, 'northeast', 3, 'Erie Drift Plain'],
  [150, 159, 'Western Pennsylvania', 40.44, -80.0, 6.5, 'mid-atlantic', 5, 'Western Allegheny Plateau'],
  [160, 168, 'Northwestern Pennsylvania', 41.2, -78.5, 5.5, 'mid-atlantic', 4, 'North Central Appalachians'],
  [169, 179, 'Central Pennsylvania', 40.3, -76.9, 6.5, 'mid-atlantic', 5, 'Ridge and Valley'],
  [180, 189, 'The Lehigh Valley', 40.7, -75.4, 6.5, 'mid-atlantic', 5, 'Ridge and Valley'],
  [190, 196, 'Greater Philadelphia', 39.95, -75.16, 7.5, 'mid-atlantic', 6, 'Northern Piedmont'],
  [197, 199, 'Delaware', 39.7, -75.5, 7.5, 'mid-atlantic', 6, 'Middle Atlantic Coastal Plain'],
  [200, 205, 'Washington, DC', 38.9, -77.03, 7.5, 'mid-atlantic', 7, 'Northern Piedmont'],
  [206, 212, 'Central Maryland', 39.3, -76.6, 7.5, 'mid-atlantic', 6, 'Northern Piedmont'],
  [214, 219, 'The Chesapeake and Western Maryland', 39.3, -77.2, 7.0, 'mid-atlantic', 6, 'Ridge and Valley'],
  [220, 229, 'Northern Virginia', 38.85, -77.3, 7.5, 'mid-atlantic', 7, 'Northern Piedmont'],
  [230, 239, 'Richmond and Tidewater', 37.5, -77.4, 7.5, 'mid-atlantic', 7, 'Middle Atlantic Coastal Plain'],
  [240, 246, 'Southwestern Virginia', 37.2, -80.0, 6.5, 'mid-atlantic', 5, 'Ridge and Valley'],
  [247, 268, 'West Virginia', 38.6, -80.5, 6.0, 'mid-atlantic', 5, 'Central Appalachians'],
  [270, 279, 'The North Carolina Piedmont', 35.9, -79.0, 7.5, 'southeast', 7, 'Piedmont'],
  [280, 286, 'Charlotte and the Carolina Piedmont', 35.4, -80.8, 7.5, 'southeast', 8, 'Piedmont'],
  [287, 289, 'The North Carolina Blue Ridge', 35.6, -82.5, 6.5, 'southeast', 5, 'Blue Ridge'],
  [290, 299, 'South Carolina', 33.8, -81.0, 8.0, 'southeast', 8, 'Southeastern Plains'],
  [300, 312, 'Atlanta and North Georgia', 33.75, -84.4, 8.0, 'southeast', 8, 'Piedmont'],
  [313, 319, 'South Georgia', 31.6, -83.9, 8.5, 'southeast', 9, 'Southeastern Plains'],
  [320, 329, 'North Florida', 30.3, -81.7, 9.0, 'florida', 10, 'Southern Coastal Plain'],
  [330, 334, 'South Florida', 26.1, -80.2, 10.5, 'florida', 11, 'Southern Florida Coastal Plain'],
  [335, 339, 'Tampa Bay and Southwest Florida', 27.9, -82.5, 10.0, 'florida', 11, 'Southern Coastal Plain'],
  [341, 342, 'Southwest Florida', 26.6, -81.9, 10.0, 'florida', 11, 'Southern Florida Coastal Plain'],
  [344, 349, 'Central Florida', 28.5, -81.4, 9.5, 'florida', 11, 'Southern Coastal Plain'],
  [350, 369, 'Alabama', 33.0, -86.8, 8.0, 'southeast', 9, 'Southeastern Plains'],
  [370, 379, 'Middle and East Tennessee', 36.0, -86.8, 7.0, 'southeast', 7, 'Interior Plateau'],
  [380, 385, 'West Tennessee', 35.2, -89.9, 7.5, 'southeast', 8, 'Mississippi Valley Loess Plains'],
  [386, 397, 'Mississippi', 32.4, -89.8, 8.0, 'southeast', 9, 'Southeastern Plains'],
  [398, 399, 'Southwest Georgia', 31.2, -84.2, 8.5, 'southeast', 9, 'Southeastern Plains'],
  [400, 427, 'Kentucky', 37.9, -85.0, 6.5, 'midwest', 6, 'Interior Plateau'],
  [430, 449, 'Central and Northern Ohio', 40.4, -82.8, 6.0, 'midwest', 5, 'Eastern Corn Belt Plains'],
  [450, 459, 'Southwestern Ohio', 39.3, -84.4, 6.5, 'midwest', 6, 'Eastern Corn Belt Plains'],
  [460, 469, 'Central Indiana', 39.8, -86.2, 6.0, 'midwest', 6, 'Eastern Corn Belt Plains'],
  [470, 479, 'Southern Indiana', 38.5, -86.5, 6.5, 'midwest', 6, 'Interior Plateau'],
  [480, 489, 'Southeastern Michigan', 42.4, -83.3, 6.5, 'midwest', 4, 'Southern Michigan/Northern Indiana Drift Plains'],
  [490, 495, 'Western Michigan', 42.9, -85.7, 6.0, 'midwest', 4, 'Southern Michigan/Northern Indiana Drift Plains'],
  [496, 499, 'Northern Michigan', 45.0, -84.7, 5.0, 'midwest', 3, 'Northern Lakes and Forests'],
  [500, 528, 'Iowa', 41.7, -93.6, 5.5, 'midwest', 6, 'Western Corn Belt Plains'],
  [530, 539, 'Southern Wisconsin', 43.1, -89.4, 5.5, 'midwest', 4, 'Southeastern Wisconsin Till Plains'],
  [540, 549, 'Northern Wisconsin', 44.8, -89.6, 4.5, 'midwest', 3, 'North Central Hardwood Forests'],
  [550, 559, 'The Twin Cities and Southern Minnesota', 44.95, -93.1, 5.0, 'midwest', 4, 'North Central Hardwood Forests'],
  [560, 567, 'Northern Minnesota', 46.8, -94.7, 4.0, 'midwest', 3, 'Northern Lakes and Forests'],
  [570, 577, 'South Dakota', 44.4, -100.3, 5.0, 'great-plains', 5, 'Northwestern Great Plains'],
  [580, 588, 'North Dakota', 47.0, -100.8, 4.0, 'great-plains', 4, 'Northwestern Great Plains'],
  [590, 599, 'Montana', 46.6, -111.0, 4.5, 'mountain-west', 3, 'Middle Rockies'],
  [600, 609, 'Chicago', 41.85, -87.7, 6.0, 'midwest', 5, 'Central Corn Belt Plains'],
  [610, 619, 'Central Illinois', 40.1, -88.3, 6.0, 'midwest', 6, 'Central Corn Belt Plains'],
  [620, 629, 'Southern Illinois', 38.6, -89.6, 6.5, 'midwest', 7, 'Interior River Valleys and Hills'],
  [630, 641, 'Missouri', 38.6, -90.2, 6.5, 'midwest', 7, 'Central Irregular Plains'],
  [644, 658, 'Southern Missouri and the Ozarks', 37.2, -93.3, 6.5, 'midwest', 7, 'Ozark Highlands'],
  [660, 679, 'Kansas', 38.5, -97.5, 6.5, 'great-plains', 8, 'Central Great Plains'],
  [680, 693, 'Nebraska', 41.0, -98.0, 5.5, 'great-plains', 7, 'Central Great Plains'],
  [700, 714, 'Louisiana', 30.4, -91.2, 9.0, 'south-central', 10, 'Mississippi Alluvial Plain'],
  [716, 729, 'Arkansas', 34.7, -92.3, 7.5, 'south-central', 8, 'Ouachita Mountains'],
  [730, 749, 'Oklahoma', 35.5, -97.5, 7.0, 'great-plains', 9, 'Central Great Plains'],
  [750, 761, 'Dallas–Fort Worth', 32.8, -96.8, 8.0, 'south-central', 9, 'Texas Blackland Prairies'],
  [762, 769, 'Central Texas', 31.5, -98.5, 8.0, 'south-central', 9, 'Cross Timbers'],
  [770, 779, 'Houston and the Gulf Coast', 29.8, -95.4, 9.0, 'south-central', 10, 'Western Gulf Coastal Plain'],
  [780, 789, 'San Antonio and Austin', 30.0, -98.0, 8.5, 'south-central', 10, 'Edwards Plateau'],
  [790, 799, 'West Texas', 32.0, -102.0, 8.0, 'southwest', 10, 'Southwestern Tablelands'],
  [800, 816, 'Colorado', 39.7, -104.9, 5.5, 'mountain-west', 4, 'Southern Rockies'],
  [820, 831, 'Wyoming', 42.8, -107.3, 4.5, 'mountain-west', 3, 'Wyoming Basin'],
  [832, 838, 'Idaho', 43.6, -116.2, 6.5, 'mountain-west', 5, 'Snake River Plain'],
  [840, 847, 'Utah', 40.76, -111.9, 6.5, 'mountain-west', 6, 'Wasatch and Uinta Mountains'],
  [850, 853, 'Phoenix', 33.45, -112.07, 9.5, 'southwest', 12, 'Sonoran Basin and Range'],
  [855, 857, 'Tucson', 32.2, -110.9, 9.0, 'southwest', 11, 'Sonoran Basin and Range'],
  [859, 865, 'Northern Arizona', 35.2, -111.7, 6.0, 'southwest', 5, 'Arizona/New Mexico Plateau'],
  [870, 884, 'New Mexico', 35.1, -106.6, 7.0, 'southwest', 8, 'Arizona/New Mexico Plateau'],
  [885, 885, 'El Paso', 31.8, -106.4, 8.5, 'southwest', 10, 'Chihuahuan Deserts'],
  [889, 891, 'Las Vegas', 36.2, -115.1, 9.0, 'southwest', 11, 'Mojave Basin and Range'],
  [893, 898, 'Northern Nevada', 39.5, -119.8, 7.0, 'mountain-west', 5, 'Central Basin and Range'],
  [900, 908, 'Los Angeles', 34.05, -118.2, 10.5, 'california', 5, 'Southern California Coast'],
  [910, 918, 'The San Gabriel Valley', 34.1, -118.0, 10.0, 'california', 7, 'Southern California Coast'],
  [919, 928, 'San Diego and the Inland Empire', 33.0, -117.1, 10.0, 'california', 7, 'Southern California Coast'],
  [930, 935, 'The California Central Coast', 35.3, -120.6, 9.5, 'california', 4, 'Central California Foothills'],
  [936, 939, 'Fresno and the San Joaquin Valley', 36.7, -119.8, 9.5, 'california', 9, 'Central California Valley'],
  [940, 951, 'The San Francisco Bay Area', 37.7, -122.3, 10.0, 'california', 3, 'Central California Foothills'],
  [952, 961, 'Sacramento and Northern California', 38.6, -121.5, 9.5, 'california', 8, 'Central California Valley'],
  [967, 968, 'Hawaii', 21.3, -157.8, 12.0, 'hawaii', 10, 'Hawaiian Islands'],
  [970, 974, 'The Willamette Valley', 45.5, -122.7, 8.5, 'pacific-northwest', 2, 'Willamette Valley'],
  [975, 979, 'Southern and Eastern Oregon', 43.2, -120.5, 7.0, 'pacific-northwest', 4, 'Northern Basin and Range'],
  [980, 986, 'Puget Sound', 47.6, -122.3, 8.5, 'pacific-northwest', 2, 'Puget Lowland'],
  [988, 994, 'Eastern Washington', 47.6, -117.4, 6.5, 'pacific-northwest', 4, 'Columbia Plateau'],
  [995, 999, 'Alaska', 61.2, -149.9, 4.5, 'alaska', 1, 'Cook Inlet'],
];

/*
 * There is deliberately no default postal code.
 *
 * A hard-coded ZIP in the field is a guess wearing the clothes of an answer: it looks like the app
 * knows where you are, and a user who presses the button without reading gets a New Jersey climate
 * applied to a garden in Oregon. The field starts empty and is filled from the browser's own position
 * when that costs nothing — see `BrowserLocation.ts` — and when it cannot be, the panel says so in
 * red rather than pretending.
 */

/** How the built-in table describes itself wherever a profile it produced is shown. */
export const POSTAL_TABLE_SOURCE = {
  id: 'zip3-table',
  label: 'Built-in ZIP district table',
  confidence: 'estimated' as const,
  attribution: 'Hardiness zones after the 2023 USDA Plant Hardiness Zone Map',
};

/** Why a postal code did not resolve, in a form the dialog can put on screen. */
export type PostalFailure = 'malformed' | 'unsupported-country' | 'not-in-table';

export interface PostalLookupResult {
  localization?: SiteLocalization;
  failure?: PostalFailure;
}

/**
 * The seam a real service implements.
 *
 * Deliberately async even though the built-in one is not: a caller written against this signature
 * does not have to change when the answer starts coming from the network, and a UI that already
 * awaits it already has its loading state.
 */
export interface PostalResolver {
  resolve(postalCode: string, countryCode?: string): Promise<PostalLookupResult>;
}

/** `08540`, `08540-1234`, ` 08540 ` → `08540`. Undefined when it is not a US ZIP at all. */
export function normalizePostalCode(input: string): string | undefined {
  const match = /^\s*(\d{5})(?:-\d{4})?\s*$/.exec(input);
  return match?.[1];
}

function findRow(prefix: number): ZoneRow | undefined {
  return ZONE_TABLE.find((row) => prefix >= row[0] && prefix <= row[1]);
}

/**
 * Resolve a US ZIP code against the built-in table.
 *
 * Synchronous, pure, and offline. Frost dates come from `estimateFrostWindow` rather than from the
 * table — see the note at the top of this file about not implying precision the block does not have.
 */
export function resolvePostalCode(input: string, countryCode = 'US'): PostalLookupResult {
  if (countryCode.toUpperCase() !== 'US') return { failure: 'unsupported-country' };

  const postalCode = normalizePostalCode(input);
  if (!postalCode) return { failure: 'malformed' };

  const row = findRow(Number(postalCode.slice(0, 3)));
  if (!row) return { failure: 'not-in-table' };

  const [, , label, latitude, longitude, zoneNumber, region, heatZone, ecoregionName] = row;
  const hardinessZone: HardinessZone = zoneFromValue(zoneNumber);
  const ecoregion: EcoregionRef = { name: ecoregionName };

  return {
    localization: {
      schemaVersion: LOCALIZATION_SCHEMA_VERSION,
      postalCode,
      countryCode: 'US',
      latitude,
      longitude,
      hardinessZone,
      frost: estimateFrostWindow(hardinessZone),
      regionLabel: label,
      nativeRegion: region,
      ecoregion,
      heatZone,
      source: { ...POSTAL_TABLE_SOURCE },
      resolvedAt: new Date().toISOString(),
    },
  };
}

/** The built-in resolver, behind the async seam a networked one would use. */
export const builtInPostalResolver: PostalResolver = {
  resolve: (postalCode, countryCode) => Promise.resolve(resolvePostalCode(postalCode, countryCode)),
};

/** One sentence explaining a failure, for the dialog. */
export function describePostalFailure(failure: PostalFailure): string {
  switch (failure) {
    case 'malformed':
      return 'That is not a five-digit US ZIP code.';
    case 'unsupported-country':
      return 'Only US ZIP codes resolve today. Everything else about the site still works.';
    case 'not-in-table':
      return (
        'That ZIP district is not in the built-in table — it covers the fifty states, not the ' +
        'territories or military addresses.'
      );
  }
}

/** Number of districts the table covers, for the "what is this" line in the UI. */
export function postalTableSize(): number {
  return ZONE_TABLE.length;
}
